import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { NoPracticeState } from "@/components/shared/NoPracticeState";
import {
  getAttestations,
  voidAttestation,
  tinLast4,
  formatAssertedDate,
  submitCmsCache,
  type AttestationRecord,
  type EvaluationType,
  type SubmitCmsClinician as Clinician,
} from "@/lib/attestations";
import {
  ArrowRight,
  CheckCircle2,
  FileSignature,
  FileText,
  Trash2,
  Users,
} from "lucide-react";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";

export default function SubmitCMS() {
  // No-practice gate (same pattern as APP Plus's NoAcoState) — synchronous,
  // no loading flash; the content component never mounts without a practice.
  const { user, loading: authLoading, selectedPractice } = useAuth();
  if (!authLoading && user !== undefined && !selectedPractice) return <NoPracticeState />;
  return <SubmitCMSContent />;
}

function SubmitCMSContent() {
  const { selectedPractice, token } = useAuth();
  const { programYear } = useProgramYear();
  const { toast } = useToast();
  const navigate = useNavigate();

  const practiceId = selectedPractice?.id;
  const entityName = selectedPractice?.tin_name || "";
  const last4 = tinLast4(selectedPractice?.tin);
  const cacheKey = `${practiceId}:${programYear}`;

  const [mode, setMode] = useState<EvaluationType>("GROUP");
  // Seed the category picker from the cached GROUP attestation so revisits
  // don't paint unchecked boxes that check themselves a frame later.
  const [categories, setCategories] = useState(() => {
    const cachedGroup = (submitCmsCache.attestations.get(cacheKey) ?? []).find(
      (a) => a.evaluation_type === "GROUP" && !a.npi
    );
    return {
      data_freeze: !!cachedGroup,
      quality: !!cachedGroup?.categories_submitted?.quality,
      ia: !!cachedGroup?.categories_submitted?.ia,
      pi: !!cachedGroup?.categories_submitted?.pi,
    };
  });

  // Stale-while-revalidate: the FIRST paint renders straight from the cache
  // (revisits show real content in the same frame — no skeleton, no flash);
  // the fetch effects below refresh in the background and update in place.
  // Skeletons only exist for the first-ever visit of the session, where they
  // paint together with the page instead of replacing content.
  const [latestAttestations, setLatestAttestations] = useState<AttestationRecord[]>(
    () => submitCmsCache.attestations.get(cacheKey) ?? []
  );
  const [loadingAttestations, setLoadingAttestations] = useState(
    () => !submitCmsCache.attestations.has(cacheKey)
  );
  const [clinicians, setClinicians] = useState<Clinician[]>(
    () => submitCmsCache.clinicians.get(cacheKey) ?? []
  );
  const [loadingClinicians, setLoadingClinicians] = useState(false);
  const [voidTarget, setVoidTarget] = useState<AttestationRecord | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);

  // In-place refresh (used after voiding) — never re-shows the skeleton.
  const reloadAttestations = async () => {
    if (!practiceId || !programYear) return;
    try {
      const res = await getAttestations(programYear, practiceId);
      const latest = res.latest || [];
      submitCmsCache.attestations.set(cacheKey, latest);
      setLatestAttestations(latest);
    } catch (err) {
      console.error("[SubmitCMS] Failed to load attestations:", err);
    }
  };

  useEffect(() => {
    if (!practiceId || !programYear) return;
    let cancelled = false;
    const key = `${practiceId}:${programYear}`;
    // Practice/year switched while mounted: swap to that key's cache first.
    const hasCache = submitCmsCache.attestations.has(key);
    setLatestAttestations(submitCmsCache.attestations.get(key) ?? []);
    setLoadingAttestations(!hasCache);
    getAttestations(programYear, practiceId)
      .then((res) => {
        if (cancelled) return;
        const latest = res.latest || [];
        submitCmsCache.attestations.set(key, latest);
        setLatestAttestations(latest);
      })
      .catch((err) => {
        console.error("[SubmitCMS] Failed to load attestations:", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingAttestations(false);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, programYear]);

  useEffect(() => {
    if (!practiceId || mode !== "NPI" || !token) return;
    let cancelled = false;
    const key = `${practiceId}:${programYear}`;
    const hasCache = submitCmsCache.clinicians.has(key);
    setClinicians(submitCmsCache.clinicians.get(key) ?? []);
    setLoadingClinicians(!hasCache);
    fetch(`${apiUrl}/api/practices/${practiceId}/eligibility-full`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: Array<{ program_year: number; clinicians: Clinician[] }>) => {
        if (cancelled) return;
        const yearEntry = data.find((d) => d.program_year === programYear) || data[0];
        const list = yearEntry?.clinicians || [];
        submitCmsCache.clinicians.set(key, list);
        setClinicians(list);
      })
      .catch((err) => {
        console.error("[SubmitCMS] Failed to load clinicians:", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingClinicians(false);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, programYear, mode, token]);

  const atLeastOneCategory = categories.quality || categories.ia || categories.pi;
  const selectionsValid = categories.data_freeze && atLeastOneCategory;

  // Look up latest attestation for the current mode (and optionally a given NPI in NPI mode).
  const findLatestFor = (npi?: string | null): AttestationRecord | undefined => {
    return latestAttestations.find(
      (a) =>
        a.evaluation_type === mode &&
        (mode === "GROUP" ? !a.npi : a.npi === npi)
    );
  };

  const groupAttestation = useMemo(
    () => (mode === "GROUP" ? findLatestFor(null) : undefined),
    [mode, latestAttestations]
  );

  // When viewing a previously-signed GROUP attestation, pre-populate the picker
  // with the categories that were signed last so Re-sign works without making
  // the user re-check every box.
  useEffect(() => {
    if (mode !== "GROUP") return;
    if (!groupAttestation) return;
    const prior = groupAttestation.categories_submitted;
    setCategories({
      data_freeze: true,
      quality: !!prior?.quality,
      ia: !!prior?.ia,
      pi: !!prior?.pi,
    });
  }, [mode, groupAttestation?.id]);

  const buildAttestationUrl = (npi?: string, clinicianName?: string) => {
    const params = new URLSearchParams({
      mode,
      year: String(programYear),
      qual: categories.quality ? "1" : "0",
      ia: categories.ia ? "1" : "0",
      pi: categories.pi ? "1" : "0",
    });
    if (mode === "NPI" && npi) {
      params.set("npi", npi);
      if (clinicianName) params.set("clinician", clinicianName);
    }
    return `/submit/attestation?${params.toString()}`;
  };

  const handleContinueGroup = () => {
    if (!selectionsValid) {
      toast({
        title: "Selections incomplete",
        description: "Acknowledge Data Freeze and choose at least one category to submit.",
        variant: "destructive",
      });
      return;
    }
    navigate(buildAttestationUrl());
  };

  const handleVoidConfirm = async () => {
    if (!voidTarget || !practiceId) return;
    const reason = voidReason.trim();
    if (!reason) {
      toast({
        title: "Reason required",
        description: "Please provide a brief reason for voiding this authorization.",
        variant: "destructive",
      });
      return;
    }
    setVoiding(true);
    try {
      await voidAttestation(voidTarget.id, reason, practiceId);
      toast({
        title: "Authorization voided",
        description:
          "Marked voided and removed from active signings. The row is retained for audit.",
      });
      setVoidTarget(null);
      setVoidReason("");
      await reloadAttestations();
    } catch (err) {
      console.error("[SubmitCMS] void failed:", err);
      toast({
        title: "Could not void authorization",
        description: err instanceof Error ? err.message : "Unexpected error",
        variant: "destructive",
      });
    } finally {
      setVoiding(false);
    }
  };

  const handleSignClinician = (c: Clinician) => {
    if (!selectionsValid) {
      toast({
        title: "Selections incomplete",
        description: "Acknowledge Data Freeze and choose at least one category before signing.",
        variant: "destructive",
      });
      return;
    }
    navigate(buildAttestationUrl(c.npi, c.clinician_name || undefined));
  };

  if (!selectedPractice) {
    return (
      <div>
        <h1 className="mb-4 text-3xl font-bold">Submit to CMS</h1>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Select a practice to continue.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="-m-6 min-h-[calc(100vh-3.5rem)] bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-900 dark:to-slate-800">
      <Helmet>
        <title>{`Submit to CMS – ${programYear} MIPS Authorization`}</title>
        <meta
          name="description"
          content="Choose what to submit to CMS and capture the signed authorization required to release your MIPS performance data."
        />
        <link rel="canonical" href="/submit" />
      </Helmet>

      <h1 className="mb-1 text-3xl font-bold">Submit to CMS</h1>
      <p className="mb-6 max-w-3xl text-sm text-muted-foreground">
        Choose what {selectedPractice.tin_name} is reporting for {programYear} and sign the
        authorization that allows A Value-Based Care Registry, a CMS-designated QCDR, to submit on your behalf.
      </p>

      {/* Mode toggle */}
      <div className="mb-6 inline-flex rounded-lg border bg-background p-1 shadow-sm">
        <button
          type="button"
          onClick={() => setMode("GROUP")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "GROUP"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Group / TIN
        </button>
        <button
          type="button"
          onClick={() => setMode("NPI")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "NPI"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Individual / NPI
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column (wider): signed banner (if any) + picker + signing card */}
        <div className="space-y-4 lg:col-span-2">
          {mode === "GROUP" && groupAttestation && (
            <Card className="border-emerald-200 bg-emerald-50/40 shadow-md">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-2 text-sm text-emerald-900">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <div>
                    Signed by{" "}
                    <span className="font-semibold">{groupAttestation.signer_name}</span>
                    {" "}on{" "}
                    <span className="font-semibold">
                      {formatAssertedDate(groupAttestation.signed_date_asserted)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => navigate(`/submit/attestation/${groupAttestation.id}`)}
                  >
                    <FileText className="mr-2 h-4 w-4" /> View signed authorization
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setVoidReason("");
                      setVoidTarget(groupAttestation);
                    }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loadingAttestations ? (
            <>
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle>What are you submitting?</CardTitle>
                  <CardDescription>Loading prior selections…</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                </CardContent>
              </Card>
              <Card className="shadow-md">
                <CardContent className="py-6">
                  <Skeleton className="h-10 w-56" />
                </CardContent>
              </Card>
            </>
          ) : (
          <>
          <Card className="shadow-md">
        <CardHeader>
          <CardTitle>What are you submitting?</CardTitle>
          <CardDescription>
            Data Freeze is required. At least one performance category must be selected. Cost is
            calculated by CMS and is not submitted from this registry.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label className="flex items-start gap-3 rounded-md border bg-muted/20 p-3">
            <Checkbox
              checked={categories.data_freeze}
              onCheckedChange={(v) =>
                setCategories((prev) => ({ ...prev, data_freeze: v === true }))
              }
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <div className="font-medium">
                Data Freeze <span className="text-xs text-destructive">(required)</span>
              </div>
              <div className="text-xs text-muted-foreground">
                I confirm that data collection is complete and no further uploads or edits will be
                made for {programYear} prior to CMS submission.
              </div>
            </div>
          </Label>

          {(
            [
              { key: "quality", label: "Quality", desc: "Submit the practice's quality measure performance." },
              { key: "ia", label: "Improvement Activities", desc: "Submit attested IA activities." },
              { key: "pi", label: "Promoting Interoperability", desc: "Submit PI objectives and measures." },
            ] as const
          ).map((c) => (
            <Label key={c.key} className="flex items-start gap-3 rounded-md border p-3">
              <Checkbox
                checked={categories[c.key]}
                onCheckedChange={(v) =>
                  setCategories((prev) => ({ ...prev, [c.key]: v === true }))
                }
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <div className="font-medium">{c.label}</div>
                <div className="text-xs text-muted-foreground">{c.desc}</div>
              </div>
            </Label>
          ))}

          {!selectionsValid && (
            <p className="pt-1 text-xs text-muted-foreground">
              Acknowledge Data Freeze and pick at least one of Quality, IA, or PI to continue.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Mode-specific signing card */}
      {mode === "GROUP" ? (
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-muted-foreground" />
              {groupAttestation
                ? "Re-sign Group / TIN authorization"
                : "Group / TIN authorization"}
            </CardTitle>
            <CardDescription>
              {groupAttestation
                ? "Adjust the selections above and create a new signed authorization. The prior signature is retained for audit."
                : `One signature authorizes submission for all clinicians billing under TIN ending ${last4 || "—"}.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {!groupAttestation && (
                <Badge variant="outline" className="border-amber-400 text-amber-700">
                  Not yet signed
                </Badge>
              )}
              <div>
                <Button onClick={handleContinueGroup} disabled={!selectionsValid}>
                  {groupAttestation
                    ? "Re-sign with current selections"
                    : "Continue to authorization"}{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              Clinician authorizations
            </CardTitle>
            <CardDescription>
              Each clinician must individually authorize submission of their own MIPS data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingClinicians ? (
              <div className="py-2 text-sm text-muted-foreground">Loading clinicians…</div>
            ) : clinicians.length === 0 ? (
              <div className="rounded-md border bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
                No clinicians found for this practice in {programYear}. Import the QPP eligibility
                file from the Configuration page first.
              </div>
            ) : (
              <ul className="divide-y rounded-md border">
                {clinicians.map((c) => {
                  const signed = findLatestFor(c.npi);
                  return (
                    <li
                      key={c.npi}
                      className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {c.clinician_name || "Unnamed clinician"}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          NPI {c.npi}
                          {c.clinician_type ? ` • ${c.clinician_type}` : ""}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {signed ? (
                          <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">
                            Signed {formatAssertedDate(signed.signed_date_asserted)}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-400 text-amber-700">
                            Not signed
                          </Badge>
                        )}
                        {signed && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/submit/attestation/${signed.id}`)}
                            >
                              <FileText className="mr-1.5 h-3.5 w-3.5" /> View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setVoidReason("");
                                setVoidTarget(signed);
                              }}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant={signed ? "outline" : "default"}
                          onClick={() => handleSignClinician(c)}
                          disabled={!selectionsValid}
                        >
                          {signed ? "Re-sign" : "Sign authorization"}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
          </>
          )}
        </div>

        {/* Right column: reporting entity (auto-filled context), sticky on lg+ */}
        <div className="space-y-4 lg:col-span-1 lg:sticky lg:top-4 lg:self-start">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Reporting entity</CardTitle>
              <CardDescription>Pulled from the practice you have selected.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="mb-1 text-xs text-muted-foreground">Legal entity name</div>
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  {entityName || <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-muted-foreground">TIN (last 4)</div>
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  {last4 || <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-muted-foreground">Program year</div>
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">{programYear}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-dashed shadow-sm">
            <CardContent className="space-y-2 py-4 text-xs text-muted-foreground">
              <p>
                <span className="font-medium text-foreground">A Value-Based Care Registry</span> is a
                CMS-designated Qualified Clinical Data Registry (QCDR).
              </p>
              <p>
                Authorizations are retained for six (6) years per 42 CFR §
                414.1400(b)(3)(xiii)(C).
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Void confirmation dialog (shared for group + per-clinician) */}
      <Dialog
        open={!!voidTarget}
        onOpenChange={(open) => {
          if (!open && !voiding) {
            setVoidTarget(null);
            setVoidReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this authorization?</DialogTitle>
            <DialogDescription>
              {voidTarget?.evaluation_type === "GROUP"
                ? "The group authorization will be marked voided."
                : `The authorization for ${voidTarget?.clinician_name || "this clinician"} (NPI ${
                    voidTarget?.npi || "—"
                  }) will be marked voided.`}{" "}
              The row is retained for audit but is no longer the active signing. To replace it,
              sign a new authorization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="submit-void-reason" className="text-sm">
              Reason <span className="text-destructive">(required)</span>
            </Label>
            <Textarea
              id="submit-void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Wrong title entered; superseded by corrected signing"
              maxLength={500}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setVoidTarget(null);
                setVoidReason("");
              }}
              disabled={voiding}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleVoidConfirm}
              disabled={voiding || voidReason.trim().length === 0}
            >
              {voiding ? "Deleting…" : "Delete authorization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
